import React, {useEffect, useRef, useState, useCallback} from 'react';
import {Box, Button, Stack, Typography, useMediaQuery, Alert} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import BiotechIcon from '@mui/icons-material/Biotech';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import mainLogo from '../../../../images/black_letters_with_blue_shadow.svg';
import {getClientConfig} from '../../../../config/clientThemes';
import HealthCategoryNav from '../../../../components/NavigationBar/HealthCategoryNavigationBar';
import {identifyClientType} from '../../../../utils/identifyClientType';
import {CLIENT_TYPES} from '../../../../api/clientconfig';
import ShareButtons from '../../../../components/buttons/ShareButtons';
import {useClientApi} from '../../../../api/client';

export const ShowLabTestsPage = ({
  sortedMetricsByHealthOrder,
  blurAndHide,
  handleNavigation,
  onBackBtnClick,
  getCategories,
  activeCategoryIndex,
  handleCategorySelect,
  item,
  config = {}, // Add config prop
  organization = {}, // Add organization prop
  transformedHealthMetrics = {}, // Add this prop to get full transformed data
}) => {
  const cardRef = useRef(null);
  const [ShadowClass, setShadowClass] = useState(true);
  const isSmallScreen = useMediaQuery('(max-width:499px)');
  const isGalaxyFold = useMediaQuery('(max-width:280px)');
  const [clientType, setClientType] = useState(CLIENT_TYPES.HELLO_HEALTH);
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  // Use passed config or fallback to detected client type
  const clientConfig = config || getClientConfig(clientType);

  // Get categories as array (fix for nav not showing)
  const categories = typeof getCategories === 'function' ? getCategories() : [];
  console.log('Categories:', categories, 'Type:', typeof categories, 'Is Array:', Array.isArray(categories));

  // State for lab tests from API
  const [labTests, setLabTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Get API functions
  const {getLabTestRecommendations} = useClientApi(CLIENT_TYPES.HELLO_HEALTH);

  const cssToBlurAndHideCard = {
    filter: blurAndHide ? 'blur(10px)' : 'blur(0px)',
    pointerEvents: blurAndHide ? 'none' : 'auto',
  };

  // Set client type once on mount - use dynamic detection or passed organization
  useEffect(() => {
    if (organization?.type) {
      // Use organization type if available
      setClientType(organization.type);
    } else {
      // Fallback to detection
      setClientType(identifyClientType());
    }
  }, [organization?.type]);

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchLabTestRecommendations = useCallback(async () => {
    if (!getLabTestRecommendations) {
      console.log('API function not available yet');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get assessment ID from localStorage and convert to integer
      const assessmentIdString = localStorage.getItem('hellohealth-assessment-id');
      const assessmentId = parseInt(assessmentIdString, 10);

      console.log('Assessment ID from localStorage:', assessmentIdString);
      console.log('Parsed Assessment ID:', assessmentId);

      if (!assessmentId || isNaN(assessmentId)) {
        // If no assessment ID, fall back to mock data based on health metrics
        console.log('No assessment ID found, using fallback data based on health metrics');
        const fallbackTests = getFallbackLabTests(sortedMetricsByHealthOrder);
        setLabTests(fallbackTests);
        setLoading(false);
        setHasInitialized(true);
        return;
      }

      console.log('Calling API with assessment ID:', assessmentId);
      const response = await getLabTestRecommendations(assessmentId);
      console.log('API Response:', response);

      // Handle response data
      const testsData = response?.data || response;

      if (!Array.isArray(testsData)) {
        console.error('Invalid response format:', testsData);
        throw new Error('Invalid response format');
      }

      // Transform API response and filter out tests with null links
      const transformedTests = testsData
        .filter(test => test.lab_test_link && test.lab_test_link !== null) // Filter out tests with null links
        .map((test, index) => ({
          id: test.id || `test-${index}`,
          assessment_id: test.assessment_id,
          name: test.recommended_test || 'Unknown Test',
          description: test.reason || 'No description available',
          link: test.lab_test_link,
          image: test.lab_test_image, // Keep image even if null, we'll handle it in rendering
        }));

      console.log('Transformed tests data:', testsData);
      console.log('Filtered and transformed tests:', transformedTests);
      setLabTests(transformedTests);
    } catch (err) {
      console.error('Error fetching lab tests:', err);
      // Fall back to mock data on error
      const fallbackTests = getFallbackLabTests(sortedMetricsByHealthOrder);
      setLabTests(fallbackTests);
      setError(null); // Don't show error since we have fallback
    } finally {
      setLoading(false);
      setHasInitialized(true);
    }
  }, [getLabTestRecommendations, sortedMetricsByHealthOrder]);

  // Only fetch once when component mounts and API is available
  useEffect(() => {
    if (!hasInitialized) {
      console.log('Initializing lab test fetch...');
      fetchLabTestRecommendations();
    }
  }, [hasInitialized, fetchLabTestRecommendations]);

  const handleRetry = () => {
    setHasInitialized(false);
    fetchLabTestRecommendations();
  };

  // Debug logging
  console.log('Component render - Loading:', loading, 'Error:', error, 'Lab Tests Count:', labTests.length);

  // Handle Learn More navigation - similar to products slide
  const handleLearnMoreNavigation = useCallback(() => {
    console.log('Lab Tests Learn More clicked');
    console.log('transformedHealthMetrics:', transformedHealthMetrics);
    console.log('sortedMetricsByHealthOrder:', sortedMetricsByHealthOrder);

    // First try to use the full transformed health metrics
    if (transformedHealthMetrics?.physicalHealth) {
      const physicalHealthItem = transformedHealthMetrics.physicalHealth;
      console.log('Using transformed Physical Health data:', physicalHealthItem);

      // Create analysis item with full transformed data
      const analysisItem = {
        ...physicalHealthItem, // This contains name, value, rating, results, etc.
        isGameCompleted: true,
        userAssessmentID: localStorage.getItem('session-id') || 'default',
        cardKey: 'physicalHealth',
        category: physicalHealthItem.name,
        healthName: physicalHealthItem.name,
        fieldValue: physicalHealthItem.value,
        healthOrderNo: physicalHealthItem.value,
      };

      console.log('Final analysis item with transformed data:', analysisItem);

      // Navigate to analysis instead of external link
      window.location.href = '/results/analysis';
      return;
    }

    console.log('Transformed data not available, using fallback...');

    // Fallback to sortedMetricsByHealthOrder
    const physicalHealthItem = sortedMetricsByHealthOrder?.find(
      item =>
        item.healthName === 'Physical Health' ||
        item.category === 'Physical Health' ||
        item.cardKey === 'physicalHealth' ||
        item.healthName?.toLowerCase().includes('physical'),
    );

    if (physicalHealthItem) {
      // Try to get full transformed data for this item
      const fullTransformedItem = transformedHealthMetrics?.[physicalHealthItem.cardKey];
      console.log('Full transformed item for cardKey:', physicalHealthItem.cardKey, fullTransformedItem);

      const analysisItem = {
        name: physicalHealthItem.healthName || physicalHealthItem.category,
        value: physicalHealthItem.fieldValue,
        rating: physicalHealthItem.fieldValue,
        cardKey: physicalHealthItem.cardKey,
        category: physicalHealthItem.category,
        healthName: physicalHealthItem.healthName,
        fieldValue: physicalHealthItem.fieldValue,
        healthOrderNo: physicalHealthItem.healthOrderNo,
        isGameCompleted: true,
        userAssessmentID: localStorage.getItem('session-id') || 'default',
        results: fullTransformedItem?.results || [],
        resultHeading: fullTransformedItem?.resultHeading || `Your ${physicalHealthItem.healthName} Metrics`,
      };

      console.log('Final analysis item with fallback data:', analysisItem);

      // Navigate to analysis instead of external link
      window.location.href = '/results/analysis';
    } else {
      // Final fallback: call the original handler if provided
      console.log('No health items found, calling original handler');
      if (typeof handleNavigation === 'function') {
        handleNavigation();
      }
    }
  }, [transformedHealthMetrics, sortedMetricsByHealthOrder, handleNavigation]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#FFF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '90%',
          padding: isLargeScreen ? '20px' : isSmallScreen ? '4px 20px' : '10px 20px',
          minHeight: '300px',
        }}
        className="results-page-wrapper"
      >
        <Typography>Loading lab test recommendations...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          backgroundColor: '#FFF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '90%',
          padding: '20px',
          minHeight: '300px',
          textAlign: 'center',
        }}
        className="results-page-wrapper"
      >
        <Alert severity="error" sx={{mb: 2, maxWidth: '500px'}}>
          {error}
        </Alert>
        <Button onClick={handleRetry} variant="contained" startIcon={<RefreshIcon />}>
          Try Again
        </Button>
      </Box>
    );
  }

  // Main render - Show up to 3 tests, but show whatever is available if less than 3
  const renderLabTests = () => {
    if (labTests.length === 0) {
      return (
        <Box sx={{height: '150px', textAlign: 'center', padding: '20px 0px'}}>
          <Typography variant="body1" gutterBottom>
            No Lab Test Recommendations Found
          </Typography>
        </Box>
      );
    }

    // Show up to 3 lab tests, or whatever is available
    const testsToShow = labTests.slice(0, 3);

    // Array of icons to cycle through
    const testIcons = [BiotechIcon, MedicalInformationIcon, HealthAndSafetyIcon];

    return testsToShow.map((test, index) => {
      // Get the corresponding icon (cycles through the 3 icons)
      const IconComponent = testIcons[index % testIcons.length];

      return (
        <Box
          key={test.id}
          sx={{
            borderRadius: '15px',
            padding: isSmallScreen ? '1px 12px 5px 12px' : '4px 14px',
            ...cssToBlurAndHideCard,
          }}
        >
          <Stack display="flex" flexDirection="row" justifyContent="space-between" gap={1}>
            <Stack flex="6">
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '13px' : '16px',
                  fontWeight: 600,
                  color: '#000000',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  WebkitLineClamp: 1,
                }}
              >
                {test.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '11px' : '14px',
                  fontWeight: 500,
                  color: '#000000',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: isSmallScreen ? 1.2 : 1.3,
                  WebkitLineClamp: 2,
                }}
              >
                {test.description}
              </Typography>
              <Button
                onClick={() => {
                  if (test.link) {
                    const link = document.createElement('a');
                    link.href = test.link;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.click();
                  }
                }}
                sx={{
                  color: '#F5733B',
                  fontSize: isSmallScreen ? '12px' : '15px',
                  display: 'flex',
                  justifyContent: 'start',
                  padding: '4px 4px 4px 0px',
                  '&:hover': {
                    backgroundColor: 'none',
                    background: 'transparent',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '20px',
                    backgroundColor: '#F5733B',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#F5733B',
                    justifyContent: 'center',
                    marginRight: '6px',
                    width: isSmallScreen ? '22px' : '28px',
                    height: isSmallScreen ? '22px' : '28px',
                    '&:hover': {
                      backgroundColor: '#F5733B',
                    },
                  }}
                >
                  <ScienceIcon sx={{color: 'white', fontSize: isSmallScreen ? '14px' : '18px'}} />
                </Typography>
                View Test
              </Button>
            </Stack>
            <Stack display="flex" justifyContent="center" alignItems="center" flex="3">
              <Box>
                {/* Show image if available, otherwise show icon */}
                {test.image && test.image !== null ? (
                  <img
                    src={test.image}
                    style={{width: isSmallScreen ? '40px' : '50px', height: 'auto'}}
                    alt={test.name || 'Lab Test'}
                  />
                ) : (
                  <IconComponent
                    sx={{
                      color: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#00BFA5',
                      fontSize: isSmallScreen ? '28px' : '38px',
                    }}
                  />
                )}
              </Box>
            </Stack>
          </Stack>
        </Box>
      );
    });
  };

  return isLargeScreen ? (
    <Stack className="result-page-card" padding={3}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
        className="results-page-wrapper"
      >
        <Stack className="result-page-card large-card" padding={isGalaxyFold ? 1 : 2}>
          <Stack className="large-result-component">
            <Stack
              sx={{
                minHeight: '380px !important',
              }}
              className={ShadowClass ? 'result-component card-gradient large' : 'result-component large'}
            >
              {renderLabTests()}
              <Box className="result-logo" sx={{marginBottom: '0px', paddingTop: '4px'}}>
                <img src={clientConfig.logos?.main} height={clientConfig.logos?.height} alt="Logo" />
                {clientType !== CLIENT_TYPES.DEFAULT && (
                  <Typography sx={{fontSize: '14px'}}>
                    Your health scan was powered by
                    <img src={mainLogo} className="custom-main-logo-for-md" alt="Main Logo" />
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  ) : (
    <Box
      sx={{
        backgroundColor: '#FFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '90%',
        padding: isLargeScreen ? '20px' : isSmallScreen ? '4px 20px' : '10px 20px',
      }}
      className="results-page-wrapper"
    >
      <Stack className="result-page-card" sx={{backgroundColor: '#fff'}}>
        <Box style={{textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'space-between'}}>
          <Stack onClick={onBackBtnClick} display="flex" flexDirection="row" style={{cursor: 'pointer'}}>
            {clientConfig?.backButton}
          </Stack>
        </Box>
        <Stack>
          <Stack>
            <Typography
              fontSize={isSmallScreen ? '14' : '18px'}
              fontWeight="700"
              color="#545454"
              textAlign="center"
              marginTop={isSmallScreen ? '2px' : '4px'}
              lineHeight={isSmallScreen ? '16px' : '30px'}
              marginBottom="4px"
            >
              Lab Tests For You
            </Typography>
            <Typography
              sx={{
                color: '#545454',
                fontFamily: 'Poppins',
                fontSize: isSmallScreen ? '12px' : '14px',
                fontWeight: 600,
                lineHeight: isSmallScreen ? 1.2 : 1.5,
                textAlign: 'center',
              }}
            >
              Based on your health assessment, these lab tests could provide valuable insights.
            </Typography>
          </Stack>

          <Stack
            ref={cardRef}
            sx={{position: 'relative', bottom: '25px'}}
            className={`${ShadowClass ? 'result-component card-gradient' : 'result-component'} result-component-org`}
          >
            {renderLabTests()}
            <Box className="result-logo" sx={{bottom: '6px !important', paddingTop: '4px !important'}}>
              <img src={clientConfig.logos?.main} height={clientConfig.logos?.height} alt="Logo" />
              {clientType !== CLIENT_TYPES.DEFAULT && (
                <Typography
                  sx={{
                    fontSize: isGalaxyFold ? '8px' : '10px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    paddingTop: '8px',
                    justifyContent: 'center',
                  }}
                >
                  Your health scan was powered by
                  <img src={mainLogo} className="custom-main-logo" alt="Main Logo" />
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* Health Category Navigation */}
        {categories.length > 0 && (
          <Box sx={{position: 'relative', bottom: '25px'}}>
            <HealthCategoryNav
              categories={categories} // Use categories array instead of function
              activeIndex={activeCategoryIndex}
              onSelectCategory={handleCategorySelect}
              isLargeScreen={false}
              clientBaseNavCss={{
                arrowColor: clientConfig?.resultPageSlider?.arrowColor,
                arrowHOverColor: clientConfig?.resultPageSlider?.arrowHOverColor,
                movingIndicatorColor: clientConfig?.resultPageSlider?.movingIndicatorColor,
                startIconColorActive: clientConfig?.resultPageSlider?.startIconColorActive,
              }}
            />
          </Box>
        )}

        {/* Disclaimer Text */}
        <Box sx={{position: 'relative', bottom: '25px', mt: 2}}>
          <Typography
            color="black"
            variant="p"
            component="p"
            fontWeight="400"
            sx={{
              fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '12px',
              textAlign: 'center',
              px: 2,
            }}
          >
            These numbers are only estimates and are not a substitute for the judgment of a healthcare professional.
            They are intended to improve awareness and general wellness.
          </Typography>
        </Box>

        <Box sx={{position: 'relative', bottom: '15px'}}>
          <Button
            fullWidth
            onClick={() => {
              window.open('https://www.gethellohealth.com/collections/lab-tests-1', '_blank');
            }}
            className={'btn-shadow'}
            sx={{
              height: isSmallScreen ? '50px' : '60px',
              borderRadius: '10px',
              marginBottom: isSmallScreen ? '6px' : '10px',
              background: clientConfig.button?.background,
              maxWidth: '400px',
              textAlign: 'center',
              color: '#fff',
              '&:hover': {
                color: 'black',
              },
            }}
          >
            <Typography
              className="btn-txt"
              variant="p"
              component="p"
              fontWeight="600"
              sx={{
                fontSize: isSmallScreen ? '16px' : '20px',
              }}
            >
              See All Tests
            </Typography>
          </Button>

          <ShareButtons socialButtonsCss={clientConfig?.socialButtons} cardRef={cardRef} />
        </Box>
      </Stack>
    </Box>
  );
};

// Fallback function to generate lab tests based on health metrics when API is not available
function getFallbackLabTests(sortedMetricsByHealthOrder) {
  const labTestDatabase = {
    'Heart Health': {
      id: 'heart-1',
      name: 'Cardiovascular Panel',
      description: 'Comprehensive heart health assessment including cholesterol and triglycerides',
      link: 'https://www.gethellohealth.com/collections/lab-tests-1',
      image: null,
    },
    'Metabolic Health': {
      id: 'metabolic-1',
      name: 'Metabolic Panel Plus',
      description: 'Complete metabolic function testing with glucose and insulin markers',
      link: 'https://www.gethellohealth.com/collections/lab-tests-1',
      image: null,
    },
    'Physical Health': {
      id: 'physical-1',
      name: 'Wellness Panel',
      description: 'Full body health screening including vitamin levels and thyroid function',
      link: 'https://www.gethellohealth.com/collections/lab-tests-1',
      image: null,
    },
    'Overall Disease Risk': {
      id: 'disease-1',
      name: 'Disease Risk Assessment',
      description: 'Comprehensive testing for cardiovascular and diabetes risk factors',
      link: 'https://www.gethellohealth.com/collections/lab-tests-1',
      image: null,
    },
    'Body Function': {
      id: 'body-1',
      name: 'Body Function Panel',
      description: 'Tests for stress markers, inflammation, and overall body function',
      link: 'https://www.gethellohealth.com/collections/lab-tests-1',
      image: null,
    },
  };

  const tests = [];

  // Get tests based on the weakest health categories (up to 3)
  if (sortedMetricsByHealthOrder && Array.isArray(sortedMetricsByHealthOrder)) {
    sortedMetricsByHealthOrder.slice(0, 3).forEach(metric => {
      const test = labTestDatabase[metric.category];
      if (test) tests.push(test);
    });
  }

  // If no tests found, provide default recommendations
  if (tests.length === 0) {
    tests.push(
      labTestDatabase['Heart Health'],
      labTestDatabase['Metabolic Health'],
      labTestDatabase['Physical Health'],
    );
  }

  return tests.filter(Boolean); // Remove any null/undefined entries
}

export default ShowLabTestsPage;
